import { useGetAllDocumentsQuery } from '@entities/common/commonQueries/index.ts';
import cn from 'classnames';
import type { FC } from 'react';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { SmallMark } from '@shared/assets';
import { useAppDispatch, usePlatformSettings } from '@shared/hooks/index.ts';
import type { RegistrationMethod } from '@shared/types/common';
import { CommonButton, IconButton } from '@shared/ui';
import { closeAuthModal } from '@src/library/redux/modal/index.ts';
import { AuthForm } from '@src/modules/Authentication/AuthFrom';
import { Captcha } from '@src/modules/Authentication/Captcha';
import CustomInput from '@src/modules/Authentication/CustomInput';
import { ModalError } from '@src/modules/Authentication/frames/modal-error';
import * as st from './styles.module.scss';

interface Props {
  model: any;
  type: RegistrationMethod;
}

export const AgreepmentStep: FC<Props> = ({ model, type }) => {
  const { data: documents } = useGetAllDocumentsQuery(undefined);
  const { is_demo } = usePlatformSettings();

  const agreementDoc = documents?.agreement;
  const offerDoc = documents?.offer;

  const privacy_policyDoc = documents?.privacy_policy;
  const advertising_policyDoc = documents?.advertising_policy;
  const dispatch = useAppDispatch();
  const errorMessage =
    model.phoneNumberErrors?.phonenumber?.message || model.phoneNumberErrors?.root?.message;
  const isCaptchaRequired = type !== 'INCOMING_SMS';
  return (
    <AuthForm
      onSubmit={model.handleSubmit((data: any) => model.checkedPhone(data.phonenumber, type))}
    >
      <div className={st.block__up}>
        <CustomInput
          tabindex={0}
          type="phonenumber"
          label="Телефон"
          name="phonenumber"
          control={model.controlPhoneNumber}
          error={model.phoneNumberErrors?.phonenumber}
          disabled={model.isLoading}
          variant="outline"
        />

        <div className={st.agreementWrapper}>
          <div className={st.agreement}>
            <div>
              <Controller
                name="acceptAgreement"
                control={model.controlPhoneNumber}
                render={({ field: { value } }) => (
                  <IconButton
                    tabIndex={0}
                    type="button"
                    aria-checked={value}
                    variant={value ? 'active' : 'outlined'}
                    size="agreement"
                    className={cn(
                      st.checkbox,
                      value && st.checked,
                      model.phoneNumberErrors.acceptAgreement && !value && st.error
                    )}
                    onClick={() => {
                      model.setFirstStepValue('acceptAgreement', !value, { shouldValidate: true });
                    }}
                  >
                    <SmallMark />
                  </IconButton>
                )}
              />
            </div>
            <div className={st.agree}>
              <span>Я принимаю условия </span>

              <Link
                // wbb - withoutBackButton
                to={`/${agreementDoc?.id}`}
                // target={'_blank'}
                state={{ withoutBackButton: true }}
                tabIndex={0}
                className={st.agreement__link}
                onClick={(e) => {
                  !agreementDoc && e.preventDefault();
                  dispatch(closeAuthModal());
                }}
              >
                пользовательского соглашения
              </Link>
              {!is_demo && (
                <>
                  <span> и согласен с условиями </span>

                  <Link
                    // wbb - withoutBackButton
                    to={`/${offerDoc?.id}`}
                    // target={'_blank'}
                    state={{ withoutBackButton: true }}
                    tabIndex={0}
                    className={st.agreement__link}
                    onClick={(e) => {
                      if (offerDoc) {
                        dispatch(closeAuthModal());
                      } else {
                        e.preventDefault();
                      }
                    }}
                    // onClick={model.openPersonalDate}
                  >
                    оферты
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className={st.agreement}>
            <div>
              <Controller
                name="acceptPersonalData"
                control={model.controlPhoneNumber}
                render={({ field: { value } }) => (
                  <IconButton
                    tabIndex={0}
                    type="button"
                    aria-checked={value}
                    variant={value ? 'active' : 'outlined'}
                    size="agreement"
                    className={cn(
                      st.checkbox,
                      value && st.checked,
                      model.phoneNumberErrors.acceptPersonalData && !value && st.error
                    )}
                    onClick={() => {
                      model.setFirstStepValue('acceptPersonalData', !value, {
                        shouldValidate: true,
                      });
                    }}
                  >
                    <SmallMark />
                  </IconButton>
                )}
              />
            </div>
            <div className={st.agree}>
              <span>Даю согласие на </span>

              <Link
                // wbb - withoutBackButton
                to={`/${privacy_policyDoc?.id}`}
                // target={'_blank'}
                state={{ withoutBackButton: true }}
                tabIndex={0}
                className={st.agreement__link}
                onClick={(e) => {
                  if (privacy_policyDoc) {
                    dispatch(closeAuthModal());
                  } else {
                    e.preventDefault();
                  }
                }}
              >
                обработку и хранение моих персональных данных
              </Link>
            </div>
          </div>

          <div className={st.agreement}>
            <div>
              <Controller
                name="acceptAds"
                control={model.controlPhoneNumber}
                render={({ field: { value } }) => (
                  <IconButton
                    tabIndex={0}
                    type="button"
                    aria-checked={value}
                    variant={value ? 'active' : 'outlined'}
                    size="agreement"
                    className={cn(
                      st.checkbox,
                      value && st.checked,
                      model.phoneNumberErrors.acceptAds && !value && st.error
                    )}
                    onClick={() => {
                      model.setFirstStepValue('acceptAds', !value, { shouldValidate: true });
                    }}
                  >
                    <SmallMark />
                  </IconButton>
                )}
              />
            </div>

            <div className={st.agree}>
              <span>Даю </span>

              <Link
                // wbb - withoutBackButton
                to={`/${advertising_policyDoc?.id}`}
                // target={'_blank'}
                state={{ withoutBackButton: true }}
                tabIndex={0}
                className={st.agreement__link}
                onClick={(e) => {
                  if (advertising_policyDoc) {
                    dispatch(closeAuthModal());
                  } else {
                    e.preventDefault();
                  }
                }}
              >
                согласие на рекламную рассылку
              </Link>
            </div>
          </div>
        </div>
      </div>

      {isCaptchaRequired && <Captcha onChange={model.onCaptchaChange} />}

      <div className={st.wrapper}>
        {errorMessage ? (
          <ModalError error={errorMessage} />
        ) : (
          <CommonButton
            tabIndex={0}
            type="submit"
            disabled={
              model.isVerificationLoading ||
              (isCaptchaRequired ? !model.isCaptchaVerified : !model.isFirstStepFormValid)
            }
          >
            Далее
          </CommonButton>
        )}
      </div>
    </AuthForm>
  );
};
